// @flow

import type {ReduxActionType} from "../../flow/ReduxActionType";

/**
 * Load translation
 * @type: Action
 */
export const translationLoad = (domain_id: number, id: number): ReduxActionType => ({
    type: 'TRANSLATION_LOAD',
    payload: { domain_id, id },
});

/**
 * Translation loaded
 * @type: Event
 */
export const translationLoaded = (translation: {}): ReduxActionType => ({
    type: 'TRANSLATION_LOADED',
    payload: translation ,
});

/**
 * Change input content
 * @type: Action
 */
export const translationChangeInput = (name: string, value: all, type: string): ReduxActionType => ({
    type: 'TRANSLATION_CHANGE_INPUT',
    payload: { name, value, type },
});

/**
 * Select which level (base / ai / manual) visitors should see for this text.
 * Nothing is sent to the API until the user saves.
 * @type: Action
 */
export const translationSelectLevel = (level: 'base' | 'ai' | 'manual'): ReduxActionType => ({
    type: 'TRANSLATION_SELECT_LEVEL',
    payload: level,
});

/**
 * Copy a text (base / AI enhanced) into the manual translation field
 * @type: Action
 */
export const translationCopy = (text: string): ReduxActionType => ({
    type: 'TRANSLATION_COPY',
    payload: text,
});

/**
 * Restore one exact version: pin it as the served row right away
 * @type: Action
 */
export const translationRestore = (domain_id: number, level: 'base' | 'ai' | 'manual', active_id: number): ReduxActionType => ({
    type: 'TRANSLATION_RESTORE',
    payload: { domain_id, level, active_id },
});

/**
 * A version has been restored, carries the refreshed detail response
 * @type: Event
 */
export const translationRestored = (translation: {}): ReduxActionType => ({
    type: 'TRANSLATION_RESTORED',
    payload: translation,
});

/**
 * Clear the recorded choice of this text, so the default priority applies again
 * (DELETE /translations/override: `active_id: null` is a real choice, not a reset)
 * @type: Action
 */
export const translationResetChoice = (domain_id: number): ReduxActionType => ({
    type: 'TRANSLATION_RESET_CHOICE',
    payload: { domain_id },
});

/**
 * The choice has been cleared, carries the refreshed detail response
 * @type: Event
 */
export const translationChoiceReset = (translation: {}): ReduxActionType => ({
    type: 'TRANSLATION_CHOICE_RESET',
    payload: translation,
});

/**
 * Click on insertion
 * @type: Action
 */
export const translationClickInsertion = (type: string, id: number): ReduxActionType => ({
    type: 'TRANSLATION_CLICK_INSERTION',
    payload: { type, id },
});

/**
 * Save a translation
 * @type: Action
 */
export const translationSave = (domain_id: number, quit: boolean): ReduxActionType => ({
    type: 'TRANSLATION_SAVE',
    payload: {domain_id, quit},
});

/**
 * Translation has been saved
 * @type: Event
 */
export const translationSaved = (translation: ?{}): ReduxActionType => ({
    type: 'TRANSLATION_SAVED',
    payload: translation,
});

/**
 * Translation has been saved
 * @type: Event
 */
export const translationSavedAndQuit = (translation_id: number): ReduxActionType => ({
    type: 'TRANSLATION_SAVED_AND_QUIT',
    payload: translation_id,
});

/**
 * Translation form has been close
 * @type: Event
 */
export const translationClosed = (): ReduxActionType => ({
    type: 'TRANSLATION_CLOSED',
    payload: null,
});

/**
 * Delete a translation
 * @type: Action
 */
export const translationDelete = (domain_id: number, id: number): ReduxActionType => ({
    type: 'TRANSLATION_DELETE',
    payload: {domain_id, id},
});

/**
 * Translation deleted
 * @type: Event
 */
export const translationDeleted = (translation_id: number): ReduxActionType => ({
    type: 'TRANSLATION_DELETED',
    payload: translation_id,
});

/**
 * Request failed
 * @type: Event
 */
export const translationRequestFailed = (): ReduxActionType => ({
    type: 'TRANSLATION_REQUEST_FAILED',
    payload: null,
});
